import * as ol from "ol";
import {Storage} from '@ionic/storage';
import {EGeoType} from "./IconService";
import {GeoJSON} from "ol/format";

/**
 *
 * GeoFAMemoizer memoizes all point- and linedata pulled from the geoFA backend.
 *
 * It is a three-stage rocket, i.e. you call it like this:
 *
 * XXX.getFeature(featureID, resolution) where resolution can be {INITIAL, MINIMAL, MAXIMAL, PULLING}
 * set pulling while the data is pulled and process so we avoid doubles.
 *
 * Optimally it would work like this:
 *
 * 1) Get minimal points for the inital BBOX -> returns [[x,y,objectId],[..], ...]
 * 2) Loop through the list and pull same feature MEDIUM in batches of (n)
 * 3) Get list of features as INITIAL WHERE objectID not in list from 1)
 * 4) Loop once again through the objects that are available as INITIAL and pull MAXIMAL
 */

export enum GeoFAMemoizeRecordType {
    INITIAL,
    MINIMAL,
    MAXIMAL,
    PULLING
}

interface CachedFeature {
    featureData: any; // Serialized GeoJSON feature
    lastUpdateDateTime: string;
    cachedAt: string; // Timestamp when first cached
    featureType: EGeoType;
    memoizeType: GeoFAMemoizeRecordType;
}

// Cache expiration: 1 week in milliseconds
const CACHE_EXPIRATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

class GeoFAMemoizer {
    private storage: Storage;
    private initialized: boolean = false;
    private geoJsonFormat: GeoJSON;
    private debugMode: boolean = false;

    constructor(storage: Storage, debugMode: boolean = false) {
        this.storage = storage;
        this.geoJsonFormat = new GeoJSON();
        this.debugMode = debugMode;
        this._create().then(() => {
            if (this.debugMode) {
                console.log('GeoFAMemoizer initialized');
            }
            // Expose to window for console access
            if (typeof window !== 'undefined') {
                (window as any).__geoFAMemoizer = this;
            }
        }).catch((error) => {
            console.error('Error initializing GeoFAMemoizer:', error);
        });
    }

    /**
     * Enable or disable debug logging
     */
    setDebugMode(enabled: boolean): void {
        this.debugMode = enabled;
    }

    /**
     * Initialize storage buckets for each cache type
     */
    async _create(): Promise<void> {
        if (this.initialized) return;
        
        try {
            await this.storage.create();
            await this.storage.set('INITIAL', {});
            await this.storage.set('MINIMAL', {});
            await this.storage.set('MAXIMAL', {});
            await this.storage.set('PULLING', {});
            this.initialized = true;
        } catch (error) {
            console.error('Error creating storage buckets:', error);
            throw error;
        }
    }

    /**
     * Check if a cached feature has expired (older than 1 week)
     * @param cached - The cached feature to check
     * @returns True if expired, false otherwise
     */
    private isExpired(cached: CachedFeature): boolean {
        if (!cached.cachedAt) {
            // Legacy cache entries without cachedAt - use lastUpdateDateTime
            const cacheTime = new Date(cached.lastUpdateDateTime).getTime();
            const now = Date.now();
            return (now - cacheTime) > CACHE_EXPIRATION_MS;
        }
        
        const cacheTime = new Date(cached.cachedAt).getTime();
        const now = Date.now();
        return (now - cacheTime) > CACHE_EXPIRATION_MS;
    }

    /**
     * Get a cached feature by objectID and memoize type
     * @param objectID - The objekt_id of the feature
     * @param memoizeType - The cache level to check
     * @returns The cached feature or null if not found or expired
     */
    async getFeature(objectID: string, memoizeType: GeoFAMemoizeRecordType): Promise<ol.Feature | null> {
        await this._create();
        
        try {
            const typeKey = GeoFAMemoizeRecordType[memoizeType];
            const cache = await this.storage.get(typeKey) || {};
            
            if (cache[objectID]) {
                const cached: CachedFeature = cache[objectID];
                
                // Check if expired
                if (this.isExpired(cached)) {
                    if (this.debugMode) {
                        console.log(`⏰ Cache EXPIRED: ${objectID} from ${typeKey}`, {
                            objectID,
                            cacheType: typeKey,
                            cachedAt: cached.cachedAt || cached.lastUpdateDateTime,
                            age: Math.round((Date.now() - new Date(cached.cachedAt || cached.lastUpdateDateTime).getTime()) / (1000 * 60 * 60 * 24)) + ' days'
                        });
                    }
                    // Remove expired entry
                    delete cache[objectID];
                    await this.storage.set(typeKey, cache);
                    return null;
                }
                
                if (this.debugMode) {
                    const age = Math.round((Date.now() - new Date(cached.cachedAt || cached.lastUpdateDateTime).getTime()) / (1000 * 60 * 60 * 24));
                    console.log(`✅ Cache HIT: ${objectID} from ${typeKey}`, {
                        objectID,
                        cacheType: typeKey,
                        cachedAt: cached.cachedAt || cached.lastUpdateDateTime,
                        age: age + ' days',
                        featureType: cached.featureType
                    });
                }
                // Deserialize and return feature
                return this.deserializeFeature(cached.featureData);
            }
            
            if (this.debugMode) {
                console.log(`❌ Cache MISS: ${objectID} not found in ${typeKey}`);
            }
            return null;
        } catch (error) {
            console.error(`Error getting feature ${objectID} from cache:`, error);
            return null;
        }
    }

    /**
     * Save a single feature to cache
     * @param feature - The OpenLayers feature to cache
     * @param memoizeType - The cache level to store in
     */
    async saveFeature(feature: ol.Feature, memoizeType: GeoFAMemoizeRecordType): Promise<void> {
        await this._create();
        
        try {
            const objectID = feature.getId() as string;
            if (!objectID) {
                console.warn('Feature has no ID, cannot cache');
                return;
            }

            const typeKey = GeoFAMemoizeRecordType[memoizeType];
            const cache = await this.storage.get(typeKey) || {};
            
            // Get feature type from properties or default to Point
            const featureType = feature.get('geotype') || EGeoType.Point;
            
            const now = new Date().toISOString();
            const cached: CachedFeature = {
                featureData: this.serializeFeature(feature),
                lastUpdateDateTime: now,
                cachedAt: now, // Track when first cached for expiration
                featureType: featureType,
                memoizeType: memoizeType,
            };
            
            cache[objectID] = cached;
            await this.storage.set(typeKey, cache);
            
            if (this.debugMode) {
                console.log(`💾 Cached: ${objectID} to ${typeKey}`, {
                    objectID,
                    cacheType: typeKey,
                    featureType,
                    timestamp: cached.lastUpdateDateTime
                });
            }
        } catch (error) {
            console.error('Error saving feature to cache:', error);
            throw error;
        }
    }

    /**
     * Save multiple features to cache in batch
     * @param features - Array of OpenLayers features to cache
     * @param memoizeType - The cache level to store in
     */
    async saveFeatures(features: ol.Feature[], memoizeType: GeoFAMemoizeRecordType): Promise<void> {
        await this._create();
        
        try {
            const typeKey = GeoFAMemoizeRecordType[memoizeType];
            const cache = await this.storage.get(typeKey) || {};
            
            for (const feature of features) {
                const objectID = feature.getId() as string;
                if (!objectID) {
                    console.warn('Feature has no ID, skipping cache');
                    continue;
                }

                // Get feature type from properties or default to Point
                const featureType = feature.get('geotype') || EGeoType.Point;
                
                const now = new Date().toISOString();
                const cached: CachedFeature = {
                    featureData: this.serializeFeature(feature),
                    lastUpdateDateTime: now,
                    cachedAt: now, // Track when first cached for expiration
                    featureType: featureType,
                    memoizeType: memoizeType,
                };
                
                cache[objectID] = cached;
    }
            
            await this.storage.set(typeKey, cache);
        } catch (error) {
            console.error('Error saving features to cache:', error);
            throw error;
        }
    }

    /**
     * Check if a feature exists in cache
     * @param objectID - The objekt_id of the feature
     * @param memoizeType - The cache level to check
     * @returns True if feature exists in cache
     */
    async hasFeature(objectID: string, memoizeType: GeoFAMemoizeRecordType): Promise<boolean> {
        await this._create();
        
        try {
            const typeKey = GeoFAMemoizeRecordType[memoizeType];
            const cache = await this.storage.get(typeKey) || {};
            return objectID in cache;
        } catch (error) {
            console.error('Error checking feature in cache:', error);
            return false;
        }
    }

    /**
     * Get all cached features of a specific type
     * @param memoizeType - The cache level to retrieve from
     * @returns Array of cached features
     */
    async getAllFeatures(memoizeType: GeoFAMemoizeRecordType): Promise<ol.Feature[]> {
        await this._create();
        
        try {
            const typeKey = GeoFAMemoizeRecordType[memoizeType];
            const cache = await this.storage.get(typeKey) || {};
            const features: ol.Feature[] = [];
            
            for (const objectID in cache) {
                const cached: CachedFeature = cache[objectID];
                const feature = this.deserializeFeature(cached.featureData);
                if (feature) {
                    features.push(feature);
                }
            }
            
            return features;
        } catch (error) {
            console.error('Error getting all features from cache:', error);
            return [];
        }
    }

    /**
     * Clear cache for a specific type or all types
     * @param memoizeType - Optional cache level to clear. If not provided, clears all
     */
    async clearCache(memoizeType?: GeoFAMemoizeRecordType): Promise<void> {
        await this._create();
        
        try {
            if (memoizeType !== undefined) {
                const typeKey = GeoFAMemoizeRecordType[memoizeType];
                await this.storage.set(typeKey, {});
            } else {
                // Clear all cache types
        await this.storage.set('INITIAL', {});
        await this.storage.set('MINIMAL', {});
        await this.storage.set('MAXIMAL', {});
        await this.storage.set('PULLING', {});
    }
        } catch (error) {
            console.error('Error clearing cache:', error);
            throw error;
        }
    }

    /**
     * Remove a specific feature from cache
     * @param objectID - The objekt_id of the feature to remove
     * @param memoizeType - The cache level to remove from
     */
    async removeFeature(objectID: string, memoizeType: GeoFAMemoizeRecordType): Promise<void> {
        await this._create();
        
        try {
            const typeKey = GeoFAMemoizeRecordType[memoizeType];
            const cache = await this.storage.get(typeKey) || {};
            
            if (objectID in cache) {
                delete cache[objectID];
                await this.storage.set(typeKey, cache);
            }
        } catch (error) {
            console.error('Error removing feature from cache:', error);
            throw error;
        }
    }

    /**
     * Get cache statistics
     * @returns Object with count of features in each cache type
     */
    async getCacheStats(): Promise<{ [key: string]: number }> {
        await this._create();
        
        try {
            const stats: { [key: string]: number } = {};
            
            for (const type of [GeoFAMemoizeRecordType.INITIAL, GeoFAMemoizeRecordType.MINIMAL, 
                                 GeoFAMemoizeRecordType.MAXIMAL, GeoFAMemoizeRecordType.PULLING]) {
                const typeKey = GeoFAMemoizeRecordType[type];
                const cache = await this.storage.get(typeKey) || {};
                stats[typeKey] = Object.keys(cache).length;
            }
            
            return stats;
        } catch (error) {
            console.error('Error getting cache stats:', error);
            return {};
        }
    }

    /**
     * Get all cached object IDs for a specific cache type
     * @param memoizeType - The cache level to inspect
     * @returns Array of object IDs in that cache
     */
    async getCachedObjectIDs(memoizeType: GeoFAMemoizeRecordType): Promise<string[]> {
        await this._create();
        
        try {
            const typeKey = GeoFAMemoizeRecordType[memoizeType];
            const cache = await this.storage.get(typeKey) || {};
            return Object.keys(cache);
        } catch (error) {
            console.error('Error getting cached object IDs:', error);
            return [];
        }
    }

    /**
     * Get detailed cache information for a specific object ID
     * @param objectID - The objekt_id to inspect
     * @param memoizeType - The cache level to check
     * @returns Cache metadata or null if not found
     */
    async getCacheInfo(objectID: string, memoizeType: GeoFAMemoizeRecordType): Promise<any> {
        await this._create();
        
        try {
            const typeKey = GeoFAMemoizeRecordType[memoizeType];
            const cache = await this.storage.get(typeKey) || {};
            
            if (cache[objectID]) {
                const cached: CachedFeature = cache[objectID];
                const cachedAt = cached.cachedAt || cached.lastUpdateDateTime;
                const age = new Date().getTime() - new Date(cachedAt).getTime();
                const ageDays = Math.round(age / (1000 * 60 * 60 * 24));
                const expiresIn = CACHE_EXPIRATION_MS - age;
                const expiresInDays = Math.round(expiresIn / (1000 * 60 * 60 * 24));
                
                return {
                    objectID,
                    cacheType: typeKey,
                    cachedAt: cachedAt,
                    featureType: cached.featureType,
                    age: age,
                    ageDays: ageDays,
                    ageMinutes: Math.round(age / 60000),
                    expiresIn: expiresIn,
                    expiresInDays: expiresInDays,
                    isExpired: this.isExpired(cached)
                };
            }
            
            return null;
        } catch (error) {
            console.error('Error getting cache info:', error);
            return null;
        }
    }

    /**
     * Clean up expired cache entries
     * @param memoizeType - Optional cache level to clean. If not provided, cleans all
     * @returns Number of expired entries removed
     */
    async cleanupExpired(memoizeType?: GeoFAMemoizeRecordType): Promise<number> {
        await this._create();
        
        let removedCount = 0;
        const typesToClean = memoizeType !== undefined 
            ? [memoizeType] 
            : [GeoFAMemoizeRecordType.INITIAL, GeoFAMemoizeRecordType.MINIMAL, 
               GeoFAMemoizeRecordType.MAXIMAL, GeoFAMemoizeRecordType.PULLING];
        
        for (const type of typesToClean) {
            const typeKey = GeoFAMemoizeRecordType[type];
            const cache = await this.storage.get(typeKey) || {};
            const originalSize = Object.keys(cache).length;
            
            for (const objectID in cache) {
                if (this.isExpired(cache[objectID])) {
                    delete cache[objectID];
                    removedCount++;
                }
            }
            
            if (Object.keys(cache).length !== originalSize) {
                await this.storage.set(typeKey, cache);
            }
        }
        
        if (this.debugMode && removedCount > 0) {
            console.log(`🧹 Cleaned up ${removedCount} expired cache entries`);
        }
        
        return removedCount;
    }

    /**
     * Log all cache contents to console (for debugging)
     */
    async logAllCache(): Promise<void> {
        await this._create();
        
        console.group('🗄️ GeoFAMemoizer Cache Contents');
        
        for (const type of [GeoFAMemoizeRecordType.INITIAL, GeoFAMemoizeRecordType.MINIMAL, 
                             GeoFAMemoizeRecordType.MAXIMAL, GeoFAMemoizeRecordType.PULLING]) {
            const typeKey = GeoFAMemoizeRecordType[type];
            const cache = await this.storage.get(typeKey) || {};
            const objectIDs = Object.keys(cache);
            
            console.group(`📦 ${typeKey} (${objectIDs.length} items)`);
            if (objectIDs.length > 0) {
                for (const objectID of objectIDs) {
                    const info = await this.getCacheInfo(objectID, type);
                    if (info) {
                        const cached = (await this.storage.get(typeKey) || {})[objectID];
                        const isExpired = cached ? this.isExpired(cached) : false;
                        console.log(`  ${objectID}:`, { ...info, expired: isExpired });
                    }
                }
            } else {
                console.log('  (empty)');
            }
            console.groupEnd();
        }
        
        const stats = await this.getCacheStats();
        console.log('📊 Total Stats:', stats);
        console.groupEnd();
    }

    /**
     * Serialize OpenLayers feature to GeoJSON format for storage
     * @param feature - The OpenLayers feature to serialize
     * @returns Serialized feature data
     */
    private serializeFeature(feature: ol.Feature): any {
        try {
            // Use OpenLayers GeoJSON format to serialize
            const geoJsonFeature = this.geoJsonFormat.writeFeatureObject(feature, {
                featureProjection: 'EPSG:3857',
                dataProjection: 'EPSG:4326'
            });
            return geoJsonFeature;
        } catch (error) {
            console.error('Error serializing feature:', error);
            // Fallback: manual serialization
            const geometry = feature.getGeometry();
            const properties = feature.getProperties();
            delete properties.geometry; // Remove geometry from properties
            
            return {
                type: 'Feature',
                id: feature.getId(),
                geometry: geometry ? this.geoJsonFormat.writeGeometryObject(geometry, {
                    featureProjection: 'EPSG:3857',
                    dataProjection: 'EPSG:4326'
                }) : null,
                properties: properties,
            };
        }
    }

    /**
     * Deserialize GeoJSON feature data back to OpenLayers feature
     * @param featureData - Serialized feature data
     * @returns OpenLayers feature or null if deserialization fails
     */
    private deserializeFeature(featureData: any): ol.Feature | null {
        try {
            if (!featureData) return null;
            
            // Use OpenLayers GeoJSON format to deserialize
            const feature = this.geoJsonFormat.readFeature(featureData, {
                featureProjection: 'EPSG:3857',
                dataProjection: 'EPSG:4326'
            });
            
            return feature;
        } catch (error) {
            console.error('Error deserializing feature:', error);
            return null;
        }
    }
}

export default GeoFAMemoizer;
